<template>
  <section class="filter-and-sort-facets">
    <!--
      v-memo on a plain <div> freezes the entire subtree (UiCollapse +
      GroupDefault) when memo values haven't changed.  Vue's patch sees
      the same VNode reference and returns immediately — child components
      are never checked for update.

      Putting v-memo directly on <ui-collapse> (a component) doesn't work:
      child components can self-trigger updates via their own reactive
      dependencies (props.selectedFacets changes reference on every toggle),
      bypassing the parent's memo cache entirely (Vue issue #9558).
    -->
    <div
      v-for="group of visibleGroups"
      :key="group.slug"
      v-memo="[
        group.slug,
        group.facets.length,
        selectedSlugsPerGroup[group.slug],
        countsPerGroup[group.slug],
        searchWizardStore.showCount,
        $isDesktop,
      ]"
    >
      <ui-collapse
        :id="`facet-group-${group.slug}`"
        :selected="!!$isDesktop || group.showByDefault"
        :data-test="`facet-groups-${group.slug}`"
        class="facet-group"
        :collapse-id="`collapse-facet-group-${group.slug}`"
      >
        <template #ui-collapse-title>
          {{ group.name }}
        </template>
        <template #ui-collapse-subtitle>
          <translate
            class="selected-item-count"
            :class="{ active: (selectedPerGroup[group.slug] ?? 0) > 0 }"
            :tt="{ count: selectedPerGroup[group.slug] ?? 0 }"
          >
            {count} selected
          </translate>
        </template>
        <template #ui-collapse-body>
          <filter-and-sort-facets-group-ranked
            v-if="group.type === 'ranked'"
            :group="group"
            :counts="perGroupCounts[group.slug] || EMPTY_COUNTS"
            :initial-facet-counts="initialFacetCounts"
            :selected-facets="perGroupSelected[group.slug] || EMPTY_SELECTED"
            :show-count="searchWizardStore.showCount"
            @facets-change="facetsChange"
          />
          <filter-and-sort-facets-group-default
            v-else
            :group="group"
            :counts="perGroupCounts[group.slug] || EMPTY_COUNTS"
            :initial-facet-counts="initialFacetCounts"
            :selected-facets="perGroupSelected[group.slug] || EMPTY_SELECTED"
            :selected-subcategory-slug="selectedSubcategorySlug"
            :show-count="searchWizardStore.showCount"
            @facets-change="facetsChange"
          />
        </template>
      </ui-collapse>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, watch } from 'vue'
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import type { FacetGroup } from '~/apps/facets/domain/facetGroups'
import { logger } from '../../../../../utility/logger'

// Stable empty references for prop fallbacks in the template.
// Inline `|| {}` / `|| []` creates new references every render,
// which falsely busts v-memo and triggers unnecessary child re-renders.
const EMPTY_COUNTS: Record<string, number> = {}
const EMPTY_SELECTED: string[] = []

type FacetsChanges = Array<[string, boolean]>

const props = withDefaults(
  defineProps<{
    selectedFacets: string[]
    facetCounts: Record<string, number>
    initialFacetCounts: Record<string, number>
    removedFacet?: string
  }>(),
  {
    selectedFacets: () => [],
  },
)

const emit = defineEmits<{
  changeFacets: [facets: string[]]
}>()

const { $isDesktop } = useNuxtApp()
const facetsStore = useFacetsStoreSetup(usePinia())
const searchWizardStore = useSearchWizardStore(usePinia())
const route = useRoute()

const lang = computed(() => (route.params.lang as string) || 'en-gb')

const subcategoryFacetSlugs = computed(() => {
  const presenter = getCampsiteCategoriesPresenter(lang.value)
  return presenter.subcategoryFacetSlugs || []
})

/**
 * Currently selected subcategory facet slug (only one allowed at a time).
 * Passed as a primitive string to GroupDefault so it can do cross-group
 * subcategory awareness without needing the full selectedFacets array.
 */
const selectedSubcategorySlug = computed(() => {
  for (const slug of props.selectedFacets) {
    if (subcategoryFacetSlugs.value.includes(slug)) return slug
  }
  return ''
})

const facetGroups = computed(() => facetsStore.facetGroups || [])

/**
 * Per-group stable props — the core INP optimisation.
 *
 * Problem: GroupDefault tracks props.selectedFacets reactively.
 * When that shared array reference changes, Vue queues ALL 18
 * GroupDefault instances for re-render (~80ms), completely
 * bypassing any v-memo on the parent.
 *
 * Fix: Pass per-group arrays/objects whose references only change
 * when that specific group's data actually changed. Vue's
 * shouldUpdateComponent sees prev === next for unchanged groups
 * and skips the re-render entirely.
 */
const perGroupSelected = reactive<Record<string, string[]>>({})
const perGroupCounts = reactive<Record<string, Record<string, number>>>({})

watch(
  () => props.selectedFacets,
  (newFacets) => {
    const selectedSet = new Set(newFacets)
    for (const group of facetGroups.value) {
      const newSelected: string[] = []
      for (const facet of group.facets) {
        if (selectedSet.has(facet.slug)) newSelected.push(facet.slug)
      }
      const prev = perGroupSelected[group.slug]
      if (
        !prev ||
        prev.length !== newSelected.length ||
        prev.some((s, i) => s !== newSelected[i])
      ) {
        perGroupSelected[group.slug] = newSelected
      }
    }
  },
  { immediate: true },
)

/**
 * Deferred counts update — the core long-task avoidance.
 *
 * After the API returns new counts, re-rendering all 18 groups in one go
 * creates a 300ms+ long task (4x throttle). Chunking into batches of
 * COUNTS_BATCH_SIZE groups, each in its own idle callback, keeps individual
 * tasks short so the browser can handle user input between batches.
 *
 * First run (immediate: true) is synchronous so the initial render has counts.
 * Subsequent runs schedule batched idle callbacks with a 500ms timeout.
 */
let countsInitialised = false
let pendingCountsIds: number[] = []
const COUNTS_BATCH_SIZE = 6

function applyCountsForGroups(
  groups: FacetGroup[],
  newCounts: Record<string, number>,
  start: number,
  end: number,
) {
  for (let i = start; i < end; i++) {
    const group = groups[i]
    if (!group) continue
    const newGroupCounts: Record<string, number> = {}
    let changed = false
    const prev = perGroupCounts[group.slug]

    for (const facet of group.facets) {
      const c = newCounts[facet.slug] || 0
      if (c) newGroupCounts[facet.slug] = c
      // Compare normalised: missing key and 0 are equivalent
      if (!prev || (prev[facet.slug] || 0) !== c) changed = true
    }

    if (changed || !prev) {
      perGroupCounts[group.slug] = newGroupCounts
    }
  }
}

function cancelPendingCounts() {
  for (const id of pendingCountsIds) cancelIdleCallback(id)
  pendingCountsIds = []
}

watch(
  () => props.facetCounts,
  (newCounts) => {
    if (!countsInitialised) {
      // First run: synchronous so SSR hydration has counts
      countsInitialised = true
      const groups = facetGroups.value
      applyCountsForGroups(groups, newCounts, 0, groups.length)
      return
    }

    // Cancel any in-flight batches from a previous counts change
    cancelPendingCounts()

    // Schedule batched idle callbacks — each processes COUNTS_BATCH_SIZE groups
    const groups = facetGroups.value
    for (let i = 0; i < groups.length; i += COUNTS_BATCH_SIZE) {
      const start = i
      const end = Math.min(i + COUNTS_BATCH_SIZE, groups.length)
      const id = requestIdleCallback(
        () => applyCountsForGroups(groups, newCounts, start, end),
        { timeout: 500 },
      )
      pendingCountsIds.push(id)
    }
  },
  { immediate: true },
)

const selectedPerGroup = computed((): Record<string, number> => {
  const counts: Record<string, number> = {}
  for (const group of facetGroups.value) {
    let count = 0
    for (const facet of group.facets) {
      if (props.selectedFacets.includes(facet.slug)) {
        count++
      }
    }
    counts[group.slug] = count
  }
  return counts
})

/**
 * Per-group string encoding facet counts + initial counts for v-memo.
 * Object references fail v-memo's === check even when the underlying
 * data hasn't changed. Converting to per-group primitives ensures only
 * groups with actual count changes re-render.
 *
 * IMPORTANT: Reads from `perGroupCounts` (deferred reactive cache), NOT
 * from `props.facetCounts`. This ensures the v-memo only breaks when the
 * deferred idle callback has actually applied the new counts — keeping the
 * 18-group re-render out of the interaction's main thread time.
 */
const countsPerGroup = computed((): Record<string, string> => {
  const perGroup: Record<string, string> = {}
  for (const group of facetGroups.value) {
    const groupCounts = perGroupCounts[group.slug] || {}
    const parts: string[] = []
    for (const facet of group.facets) {
      const c = groupCounts[facet.slug] || 0
      const ic = props.initialFacetCounts[facet.slug] || 0
      if (c || ic) parts.push(`${facet.slug}:${c},${ic}`)
    }
    perGroup[group.slug] = parts.join('|')
  }
  return perGroup
})

/**
 * Per-group string of selected facet slugs for v-memo.
 * Using per-group primitive keys means only the affected group re-renders
 * when a facet is toggled, instead of ALL groups re-rendering.
 */
const selectedSlugsPerGroup = computed((): Record<string, string> => {
  const selectedSet = new Set(props.selectedFacets)
  const perGroup: Record<string, string> = {}
  for (const group of facetGroups.value) {
    const slugs: string[] = []
    for (const facet of group.facets) {
      if (selectedSet.has(facet.slug)) {
        slugs.push(facet.slug)
      }
    }
    perGroup[group.slug] = slugs.join(',')
  }
  return perGroup
})

const visibleGroups = computed((): FacetGroup[] => {
  return facetGroups.value
    .map((group) => ({
      ...group,
      facets: group.hideEmpty
        ? group.facets.filter(
            (facet) =>
              // if we are not showing counts then show all
              !searchWizardStore.showCount ||
              // otherwise only show the facets that match initial counts
              props.initialFacetCounts[facet.slug] ||
              // Read from deferred cache — keeps hideEmpty filtering
              // deferred to idle time along with count badge updates
              (perGroupCounts[group.slug] || EMPTY_COUNTS)[facet.slug],
          )
        : group.facets,
    }))
    .filter(
      (group) => group.facets.length > 0 && group.slug !== 'campsite-group',
    )
})

function recalculateSelectedFacets(changes: FacetsChanges) {
  const facetsRemoved: string[] = []
  const tmpFacets = [...props.selectedFacets]

  for (const [slug, isAdded] of changes) {
    // If a subcategory facet is being added, remove any existing selected subcategory facets
    if (isAdded && subcategoryFacetSlugs.value.includes(slug)) {
      for (const existingFacet of tmpFacets) {
        if (subcategoryFacetSlugs.value.includes(existingFacet)) {
          facetsRemoved.push(existingFacet)
        }
      }
    }

    isAdded ? tmpFacets.push(slug) : facetsRemoved.push(slug)
  }

  return tmpFacets.filter((slug) => !facetsRemoved.includes(slug))
}

function facetsChange(changes: FacetsChanges) {
  logger('FilterAnSortFacets.facetsChange', changes)
  const result = recalculateSelectedFacets(changes)
  emit('changeFacets', result)
}

// Cancel any in-flight idle callbacks on unmount (SPA navigation)
onBeforeUnmount(() => {
  cancelPendingCounts()
})
</script>

<style scoped>
/*
 * CSS containment for INP — limits style recalculation scope.
 *
 * When the filter panel opens (content-visibility: hidden → visible on the
 * slideout), the browser must calculate styles for all elements inside.
 * Without containment, all 963 elements are recalculated in one pass (~974ms
 * at 4x CPU throttle). With content-visibility: auto, the browser skips
 * off-viewport groups, reducing initial recalc to only the ~5-6 visible ones.
 *
 * contain: layout style paint isolates each group so style changes in one
 * group (checkbox toggle) don't cascade to siblings.
 */
.filter-and-sort-facets > div {
  contain: layout style paint;
  content-visibility: auto;
  contain-intrinsic-size: auto 200px;
}

.filter-and-sort-facets :deep(.count) {
  margin-left: auto;
}

.filter-and-sort-facets :deep(.facets-group) {
  padding: 1em;
}

.filter-and-sort-facets :deep(.ui-collapse-header .ui-collapse-title) {
  font-weight: bold;
}

.filter-and-sort-facets :deep(.ui-collapse-subtitle) {
  flex-shrink: 0;
}

.filter-and-sort-facets :deep(.temporary-hide) {
  display: none !important;
}

.selected-item-count {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  background: var(--colour-light-yellow);
  border: 1em;
  padding: 0.2em 0.5em;
  border-radius: 1em;
  font-size: var(--small);
  transition:
    transform 80ms ease-out,
    opacity 120ms ease-out;
  transform: scale(0.75);
  opacity: 0;
}

.selected-item-count.active {
  opacity: 1;
  transform: scale(1);
}
</style>
